import { Typography } from '@mui/material';
import { useCallback } from 'react';

import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { COLORS } from '@/styles/tokens/colors';
import { diagnostics } from '@/utils/diagnostics';
import { pluralize } from '@/utils/formatting/strings';

import {
  AdminRolesPage_RoleFragment,
  Operations,
  useDeleteRoleMutation,
} from '../graphql/GetRoles.generated';

export interface DeleteRoleConfirmationModalProps {
  isOpen: boolean;
  role: AdminRolesPage_RoleFragment | undefined;
  onClose: () => void;
  onDeleted?: () => void;
}

export function DeleteRoleConfirmationModal({
  isOpen,
  role,
  onClose,
  onDeleted,
}: DeleteRoleConfirmationModalProps) {
  const { showFeedback } = useFeedback();
  const [deleteRoleMutation] = useDeleteRoleMutation();

  const handleConfirm = useCallback(async () => {
    if (!role) return;

    try {
      await deleteRoleMutation({
        variables: { roleID: role.id },
        refetchQueries: [Operations.Query.GetRoles],
      });

      showFeedback('Role deleted successfully.', { variant: 'success' });
      onClose();
      onDeleted?.();
    } catch (error) {
      diagnostics.error('Failed to delete role:', error as Error);
      showFeedback(FeedbackMessages.deleteError, { variant: 'error' });
      onClose();
    }
  }, [role, deleteRoleMutation, showFeedback, onClose, onDeleted]);

  const handleModalClose = useCallback(
    (reason: 'confirm' | 'cancel') => {
      if (reason === 'confirm') {
        void handleConfirm();
      } else {
        onClose();
      }
    },
    [handleConfirm, onClose]
  );

  if (!role) return null;
  const numberOfEmployees = role.employees?.totalCount || 0;
  const employeesNoun = pluralize({
    count: numberOfEmployees,
    singular: 'employee',
  });

  return (
    <ConfirmationModal
      isOpen={isOpen}
      heading="Delete Role"
      confirmText="Delete"
      cancelText="Cancel"
      onClose={handleModalClose}
      confirmButtonProps={{
        variant: 'destructive-primary',
      }}
    >
      <Typography variant="body-s">
        {`Are you sure you want to delete the role "${role.displayName}"? This action cannot be undone.`}
      </Typography>
      {numberOfEmployees > 0 && (
        <Typography
          variant="body-s"
          color={COLORS.FUNCTIONAL.ERROR.DEFAULT}
          sx={{ mt: 1 }}
        >
          Warning: This role is currently assigned to {employeesNoun}.
        </Typography>
      )}
    </ConfirmationModal>
  );
}
